/**
 * role接口配置
 */
import type { ApiEndpoint } from '../types';
import { API_CATEGORIES } from '../constants';

export const roleEndpoints: ApiEndpoint[] = [
  {
    id: "role-list",
    method: "GET",
    path: "/api/role/list",
    name: "获取角色列表",
    description: "分页查询角色列表，支持多条件搜索",
    category: API_CATEGORIES.ROLE,
    queryParams: [
      { name: "current", type: "number", required: false, description: "当前页码", example: "1" },
      { name: "pageSize", type: "number", required: false, description: "每页条数", example: "10" },
      { name: "name", type: "string", required: false, description: "角色名称（模糊搜索）" },
      { name: "code", type: "string", required: false, description: "角色代码（模糊搜索）" },
      { name: "status", type: "string", required: false, description: "状态（active-启用，inactive-禁用）" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.items", type: "array", description: "角色列表" },
      { name: "data.total", type: "number", description: "总记录数" },
      { name: "data.pagination", type: "object", description: "分页信息" },
      { name: "data.items[].id", type: "string", description: "角色ID" },
      { name: "data.items[].name", type: "string", description: "角色名称" },
      { name: "data.items[].code", type: "string", description: "角色代码" },
      { name: "data.items[].description", type: "string", description: "角色描述" },
      { name: "data.items[].isActive", type: "boolean", description: "是否启用" },
      { name: "data.items[].status", type: "string", description: "状态（active/inactive）" },
      { name: "data.items[].permissionCount", type: "number", description: "权限数量" },
      { name: "data.items[].adminCount", type: "number", description: "管理员数量" },
    ],
    requiredPermission: "msadmin:roles:view",
  },
  {
    id: "role-create",
    method: "POST",
    path: "/api/role/add",
    name: "创建角色",
    description: "创建新的角色",
    category: API_CATEGORIES.ROLE,
    bodyParams: [
      { name: "name", type: "string", required: true, description: "角色名称" },
      { name: "code", type: "string", required: true, description: "角色代码" },
      { name: "description", type: "string", required: false, description: "角色描述" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "新创建的角色ID" },
      { name: "data.name", type: "string", description: "角色名称" },
      { name: "data.code", type: "string", description: "角色代码" },
    ],
    requiredPermission: "msadmin:roles:create",
  },
  {
    id: "role-detail",
    method: "GET",
    path: "/api/role/:id",
    name: "获取角色详情",
    description: "根据ID获取角色详细信息",
    category: API_CATEGORIES.ROLE,
    pathParams: [{ name: "id", type: "string", required: true, description: "角色ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "角色ID" },
      { name: "data.name", type: "string", description: "角色名称" },
      { name: "data.code", type: "string", description: "角色代码" },
      { name: "data.description", type: "string", description: "角色描述" },
      { name: "data.isActive", type: "boolean", description: "是否启用" },
      { name: "data.createdAt", type: "string", description: "创建时间" },
      { name: "data.updatedAt", type: "string", description: "更新时间" },
    ],
    requiredPermission: "msadmin:roles:view",
  },
  {
    id: "role-update",
    method: "PUT",
    path: "/api/role/:id",
    name: "更新角色",
    description: "更新角色信息",
    category: API_CATEGORIES.ROLE,
    pathParams: [{ name: "id", type: "string", required: true, description: "角色ID" }],
    bodyParams: [
      { name: "name", type: "string", required: false, description: "角色名称" },
      { name: "code", type: "string", required: false, description: "角色代码" },
      { name: "description", type: "string", required: false, description: "角色描述" },
      { name: "is_active", type: "boolean", required: false, description: "是否启用" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:roles:update",
  },
  {
    id: "role-delete",
    method: "DELETE",
    path: "/api/role/:id",
    name: "删除角色",
    description: "根据ID删除角色",
    category: API_CATEGORIES.ROLE,
    pathParams: [{ name: "id", type: "string", required: true, description: "角色ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:roles:delete",
  },
  {
    id: "role-permissions",
    method: "GET",
    path: "/api/role/:id/permissions",
    name: "获取角色权限",
    description: "获取指定角色的权限列表",
    category: API_CATEGORIES.ROLE,
    pathParams: [{ name: "id", type: "string", required: true, description: "角色ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "权限列表" },
    ],
    requiredPermission: "msadmin:roles:view",
  },
  {
    id: "role-assign-permissions",
    method: "POST",
    path: "/api/role/assign-permissions",
    name: "为角色分配权限",
    description: "为指定角色分配权限",
    category: API_CATEGORIES.ROLE,
    bodyParams: [
      { name: "role_id", type: "string", required: true, description: "角色ID" },
      { name: "permission_ids", type: "array", required: true, description: "权限ID数组", example: "[\"1\", \"2\"]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:roles:assign-permissions",
  },
  {
    id: "role-batch-delete",
    method: "POST",
    path: "/api/role/batch-delete",
    name: "批量删除角色",
    description: "根据ID列表批量删除角色",
    category: API_CATEGORIES.ROLE,
    bodyParams: [{ name: "ids", type: "array", required: true, description: "角色ID数组", example: "[\"1\", \"2\", \"3\"]" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.deletedCount", type: "number", description: "删除的角色数量" },
    ],
    requiredPermission: "msadmin:roles:delete",
  },
  {
    id: "role-admins",
    method: "GET",
    path: "/api/role/admins",
    name: "获取所有管理员",
    description: "获取所有管理员列表（用于角色分配）",
    category: API_CATEGORIES.ROLE,
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "管理员列表" },
    ],
    requiredPermission: "msadmin:roles:view",
  },
  {
    id: "role-admins-list",
    method: "GET",
    path: "/api/role/:id/admins",
    name: "获取角色下的管理员",
    description: "获取指定角色下的管理员列表",
    category: API_CATEGORIES.ROLE,
    pathParams: [{ name: "id", type: "string", required: true, description: "角色ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "管理员列表" },
    ],
    requiredPermission: "msadmin:roles:view",
  },
  {
    id: "role-assign-admins",
    method: "POST",
    path: "/api/role/:id/admins",
    name: "为角色分配管理员",
    description: "为指定角色分配管理员",
    category: API_CATEGORIES.ROLE,
    pathParams: [{ name: "id", type: "string", required: true, description: "角色ID" }],
    bodyParams: [{ name: "admin_ids", type: "array", required: true, description: "管理员ID数组", example: "[\"1\", \"2\"]" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:roles:assign-admins",
  },
  {
    id: "role-remove-admin",
    method: "DELETE",
    path: "/api/role/:id/admins/:adminId",
    name: "从角色移除管理员",
    description: "从指定角色中移除管理员",
    category: API_CATEGORIES.ROLE,
    pathParams: [
      { name: "id", type: "string", required: true, description: "角色ID" },
      { name: "adminId", type: "string", required: true, description: "管理员ID" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:roles:update",
  },
];
